import { useEffect, useRef, useState } from 'react';
import { PackagePlus, X } from 'lucide-react';

// Soft two-note chime (Web Audio, no file). Browsers may block sound until the page is clicked.
function chime() {
  try {
    const ac = new (window.AudioContext || window.webkitAudioContext)();
    ac.resume?.();
    [[880, 0], [1318.5, 0.18]].forEach(([f, t]) => {
      const o = ac.createOscillator(); const g = ac.createGain();
      o.type = 'sine'; o.frequency.value = f;
      g.gain.setValueAtTime(0.0001, ac.currentTime + t);
      g.gain.exponentialRampToValueAtTime(0.25, ac.currentTime + t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ac.currentTime + t + 0.9);
      o.connect(g).connect(ac.destination); o.start(ac.currentTime + t); o.stop(ac.currentTime + t + 1);
    });
    setTimeout(() => ac.close?.(), 1500);
  } catch (e) { /* sound unavailable */ }
}

// Each time every card has (re)loaded — page refresh or the live check — compare main-portal
// pieces with what this login has already seen.
export function useNewPieces(ctx, items, ready) {
  const [fresh, setFresh] = useState(null);
  const prev = useRef(false);
  useEffect(() => {
    const was = prev.current;
    prev.current = ready;
    if (!ready || was || !ctx || !(ctx.is_tag_admin || ctx.is_tag_super)) return;
    const key = `tagws-seen:${ctx.email}:${ctx.company_id || ''}`;
    const portal = items.filter((j) => !j.is_manual);
    let seenIds = null;
    try { seenIds = JSON.parse(localStorage.getItem(key) || 'null'); } catch (e) { seenIds = null; }
    try { localStorage.setItem(key, JSON.stringify(portal.map((j) => j.id))); } catch (e) { /* storage full */ }
    if (!Array.isArray(seenIds)) return;
    const seen = new Set(seenIds);
    const added = portal.filter((j) => !j.sold && !seen.has(j.id)).map((j) => j.id);
    if (added.length) { setFresh((cur) => [...new Set([...(cur || []), ...added])]); chime(); }
  }, [ctx, items, ready]);
  return [fresh, () => setFresh(null)];
}

export function TagNewPiecesNotice({ count, onShow, onClose }) {
  return (
    <div className="fixed inset-0 z-[240] flex items-center justify-center p-4" data-testid="tagws-new-pieces-notice">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-sm rounded-2xl border border-border bg-card p-6 text-center shadow-2xl animate-in fade-in zoom-in-95">
        <button onClick={onClose} className="absolute right-3 top-3 text-muted-foreground hover:text-foreground" aria-label="Close" data-testid="tagws-new-pieces-close"><X size={18} /></button>
        <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600"><PackagePlus size={28} /></div>
        <h2 className="text-2xl font-bold text-foreground" data-testid="tagws-new-pieces-count">{count} New Piece{count === 1 ? '' : 's'} Added</h2>
        <p className="mt-1 text-sm text-muted-foreground">from the main portal since you last looked.</p>
        <div className="mt-5 flex justify-center gap-2">
          <button onClick={onClose} className="rounded-full border border-border px-4 py-2 text-sm font-semibold" data-testid="tagws-new-pieces-ok">OK</button>
          <button onClick={onShow} className="rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700" data-testid="tagws-new-pieces-show">Show new pieces</button>
        </div>
      </div>
    </div>
  );
}
